import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import DocumentLink from '../shared/DocumentLink';
import { canAccess } from '../../utils/rbac';

// The content library: approved answers, references, case studies and
// policies, tagged, each with an owner and a review date, plus QMS
// Control's accreditations read in. One searchable page (no
// new tab on the tracker), added after the market scan of 17 September 2026:
// GMP could not find our health and safety policy in the submission, and the
// missing Rolls-Royce reference cost framework places.
const TYPES = [
  { key: 'answer', label: 'Approved answers' },
  { key: 'reference', label: 'References' },
  { key: 'case_study', label: 'Case studies' },
  { key: 'policy', label: 'Policies' },
  { key: 'accreditation', label: 'Accreditations' },
];
const typeLabel = (k) => ({ answer: 'Approved answer', reference: 'Reference', case_study: 'Case study', policy: 'Policy', accreditation: 'Accreditation' }[k] || k);
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDay = (s) => { if (!s) return ''; const d = new Date(String(s).slice(0, 10) + 'T00:00:00Z'); return isNaN(d) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }); };
const reviewState = (item) => {
  if (!item.review_date) return { label: 'No review date', cls: 'bg-gray-50 text-gray-500' };
  const today = todayIso();
  if (item.review_date < today) return { label: `Review overdue (${fmtDay(item.review_date)})`, cls: 'bg-red-50 text-red-700' };
  const soon = new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);
  if (item.review_date <= soon) return { label: `Review by ${fmtDay(item.review_date)}`, cls: 'bg-amber-50 text-amber-700' };
  return { label: `Current to ${fmtDay(item.review_date)}`, cls: 'bg-emerald-50 text-emerald-700' };
};

export default function BidLibraryPage() {
  const { currentUser, navigate } = useApp();
  const isAdmin = (currentUser?.user_role || '').split(',').map(r => r.trim()).includes('admin');
  const [rows, setRows] = useState([]);
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [onlyDue, setOnlyDue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(undefined); // undefined closed, null new, obj edit
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setErr('');
    const params = {};
    if (q) params.q = q;
    if (type) params.type = type;
    api.get('crm/library', params)
      .then(d => setRows(d?.rows || []))
      .catch(e => setErr(e?.message || 'Could not load the library.'))
      .finally(() => setLoading(false));
  }, [q, type]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('people').then(d => setPeople(Array.isArray(d) ? d : (d?.rows || []))).catch(() => {}); }, []);

  const today = todayIso();
  const shown = rows.filter(r => !onlyDue || (r.review_date && r.review_date <= today));
  const dueCount = rows.filter(r => r.review_date && r.review_date <= today).length;
  const archive = async (item) => {
    if (!window.confirm(`Archive "${item.title}"? It leaves the library but is kept for the bids that used it.`)) return;
    try { await api.delete(`crm/library/${item.id}`); load(); }
    catch (e) { setErr(e?.message || 'Could not archive it.'); }
  };

  return (
    <div className="p-4 md:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h1 className="text-base font-extrabold text-navy leading-tight">Content library</h1>
          <div className="text-[10px] text-gray-500 mt-0.5">Approved answers, references, case studies, policies and accreditations, each with an owner and a review date. Point a bid's compliance matrix at the item it used.</div>
        </div>
        <button onClick={() => setEditing(null)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ Add item</button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, tags and text…" className="form-input text-[10px] w-64" />
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setType('')} className={`px-2 py-0.5 text-[9px] font-semibold rounded-full border ${!type ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>All</button>
          {TYPES.map(t => (
            <button key={t.key} onClick={() => setType(t.key)} className={`px-2 py-0.5 text-[9px] font-semibold rounded-full border ${type === t.key ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{t.label}</button>
          ))}
        </div>
        <label className="text-[9px] text-gray-600 flex items-center gap-1 ml-auto cursor-pointer">
          <input type="checkbox" checked={onlyDue} onChange={e => setOnlyDue(e.target.checked)} /> Review due{dueCount ? ` (${dueCount})` : ''}
        </label>
      </div>

      {err && <div role="alert" className="text-[10px] text-red-600 mb-2">{err}</div>}
      {loading && rows.length === 0 ? (
        <div className="text-xs text-gray-500">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-6 text-center text-[10px] text-gray-400">
          {rows.length === 0 && !q && !type ? 'Nothing in the library yet. Add the health and safety policy, the references buyers ask for, and the answers that scored well.' : 'Nothing matches.'}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded divide-y divide-gray-100">
          {shown.map(item => {
            const rs = reviewState(item);
            const isOpen = open === item.id;
            const canEdit = item.source === 'library' && (isAdmin || item.created_by === currentUser?.id || item.owner_id === currentUser?.id);
            return (
              <div key={item.id} className="px-3 py-2">
                <div className="flex items-start gap-3 flex-wrap">
                  <button onClick={() => setOpen(isOpen ? null : item.id)} className="text-left min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded bg-navy/10 text-navy">{typeLabel(item.type)}</span>
                      <span className="text-[11px] font-semibold text-gray-800">{item.title}</span>
                      {item.filename && <span className="text-[8px] text-gray-400">📎 {item.filename}</span>}
                    </div>
                    <div className="text-[9px] text-gray-500 mt-0.5">
                      {item.tags ? item.tags.split(',').map(t => <span key={t} className="inline-block mr-1 px-1.5 rounded bg-gray-50 text-gray-600">{t}</span>) : null}
                      {item.source === 'qms' ? <span>from QMS accreditations{item.owner_name ? `, owner ${item.owner_name}` : ''}</span> : item.owner_name ? <span>owner {item.owner_name}</span> : <span className="text-amber-700">no owner</span>}
                    </div>
                  </button>
                  <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${rs.cls}`}>{rs.label}</span>
                  <div className="flex items-center gap-2 text-[9px] whitespace-nowrap">
                    {item.url && <DocumentLink kind={item.source === 'qms' ? null : 'bid-library'} id={item.id} url={item.url} filename={item.filename} title={item.title} className="text-blue-700 hover:underline">View</DocumentLink>}
                    {item.source === 'qms' && canAccess('qms-admin') && <button onClick={() => navigate('qms-admin')} className="text-gray-500 hover:text-navy underline" title="Accreditations are managed in QMS Control">QMS Control</button>}
                    {canEdit && <button onClick={() => setEditing(item)} className="text-navy hover:underline font-semibold">Edit</button>}
                    {canEdit && <button onClick={() => archive(item)} className="text-gray-400 hover:text-red-500">Archive</button>}
                  </div>
                </div>
                {isOpen && (
                  <div className="text-[10px] text-gray-700 whitespace-pre-wrap mt-2 border-l-2 border-gray-100 pl-2">{item.body || <span className="text-gray-400">No text; the item is the attached file.</span>}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing !== undefined && (
        <LibraryItemModal item={editing} people={people} onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

function LibraryItemModal({ item, people, onClose, onSaved }) {
  const [f, setF] = useState({
    type: item?.type || 'answer', title: item?.title || '', tags: item?.tags || '', body: item?.body || '',
    url: item?.url || '', filename: item?.filename || '', owner_id: item?.owner_id || '', review_date: item?.review_date || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { setErr('A title is needed.'); return; }
    setSaving(true); setErr('');
    const body = { ...f, title: f.title.trim(), tags: f.tags, owner_id: f.owner_id || null, review_date: f.review_date || null, url: f.url || null, filename: f.filename || null, body: f.body || null };
    try {
      if (item) await api.put(`crm/library/${item.id}`, body);
      else await api.post('crm/library', body);
      onSaved();
    } catch (e) { setErr(e?.message || 'Could not save.'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={item ? 'Edit library item' : 'Add to the library'} onClose={onClose}
      footer={<>
        {err && <span role="alert" className="text-[9px] text-red-600 mr-auto">{err}</span>}
        <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={save} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="space-y-2 text-[10px]">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
            <select value={f.type} onChange={e => set('type', e.target.value)} className="form-select w-full text-[9px]">
              {TYPES.filter(t => t.key !== 'accreditation').map(t => <option key={t.key} value={t.key}>{typeLabel(t.key)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Review by</label>
            <input type="date" value={f.review_date} onChange={e => set('review_date', e.target.value)} className="form-input w-full text-[9px]" />
          </div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Title *</label>
          <input value={f.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Health and safety policy v7" className="form-input w-full text-[9px]" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Tags</label>
            <input value={f.tags} onChange={e => set('tags', e.target.value)} placeholder="comma separated: h&s, iso 45001" className="form-input w-full text-[9px]" />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Owner</label>
            <select value={f.owner_id} onChange={e => set('owner_id', e.target.value)} className="form-select w-full text-[9px]">
              <option value="">Nobody yet</option>
              {people.map(p => <option key={p.id} value={p.id}>{`${p.first_name || ''} ${p.last_name || ''}`.trim()}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Text</label>
          <textarea value={f.body} onChange={e => set('body', e.target.value)} rows={6} placeholder="The approved wording, the reference details, the case study." className="form-input w-full text-[9px]" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">File</label>
          {f.url ? (
            <div className="flex items-center gap-2 text-[9px]"><a href={f.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">{f.filename || 'Attached file'}</a><button onClick={() => { set('url', ''); set('filename', ''); }} className="text-gray-400 hover:text-red-500">remove</button></div>
          ) : (
            <FileUpload onUpload={r => { set('url', r.url); set('filename', r.filename); }} onClear={() => { set('url', ''); set('filename', ''); }} />
          )}
        </div>
      </div>
    </Modal>
  );
}
